import { Activity, PanelRightClose } from "lucide-react";
import { useCallback, useState } from "react";
import type { DemoCase } from "@rnavoyager/contracts";
import { TaskExecutionSteps, WorkspacePlanHeading } from "./TaskExecutionSteps";
import type { PlanStepStatus } from "./TaskExecutionSteps";
import { ExecutionActivityRow, truncatePlanObjective } from "./WorkspaceRightRail";
import type { ExecutionActivity } from "./WorkspaceRightRail";
import { WorkspaceFilesPanel } from "./WorkspaceFilesPanel";
import { demoDirectory } from "./demo-files";

export function DemoExecutionRail({
  demo,
  phase,
  playing,
  onClose,
  onOpenResult,
  fileRequest,
  onFileRequestHandled,
}: {
  demo: DemoCase;
  phase: number;
  playing: boolean;
  onClose: () => void;
  onOpenResult: (id: string) => void;
  fileRequest: { path: string } | null;
  onFileRequestHandled: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const planning = phase === -3;
  const listFiles = useCallback(
    async (_session: string, path = "/project") => demoDirectory(demo, phase, path),
    [demo, phase],
  );
  const statuses: PlanStepStatus[] = demo.stages.map((_, index) =>
    index <= phase ? "success" : playing && index === phase + 1 ? "active" : "pending",
  );
  const steps = demo.stages.map((stage) => ({
    id: stage.stageId,
    description: stage.title,
    acceptanceCriteria: stage.explanation,
  }));
  const activities: ExecutionActivity[] = demo.stages
    .slice(0, Math.max(0, phase + 1))
    .map((stage, index) => ({
      id: stage.stageId,
      actionIds: [],
      firstCursor: index,
      lastCursor: index,
      label: stage.title,
      status: "succeeded",
      statusLabel: "结果已就绪",
      detail: stage.explanation,
      startedAt: "",
      finishedAt: null,
      durationSeconds: null,
      jobId: null,
      progressUpdatedAt: null,
      progressDetail: "",
    }));
  return (
    <aside
      aria-label="任务执行与文件树"
      className={`workspace-right-rail workspace-rail${expanded ? "" : " files-collapsed"}`}
    >
      <section className="workspace-rail-card workspace-plan-panel">
        <WorkspacePlanHeading
          title={
            planning
              ? "正在规划任务"
              : phase === -2
                ? "等待任务规划"
                : truncatePlanObjective(demo.goal)
          }
          objective={demo.goal}
          actions={
            <button
              aria-label="收起右侧面板"
              className="workspace-rail-icon-button workspace-rail-close"
              onClick={onClose}
              title="收起右侧面板"
            >
              <PanelRightClose size={15} />
            </button>
          }
        />
        <div className="workspace-execution-scroll">
          {phase >= -1 ? (
            <TaskExecutionSteps
              steps={steps}
              statuses={statuses}
              onOpenStep={(index) => onOpenResult(demo.stages[index].document.documentId)}
            />
          ) : (
            <div className="workspace-plan-empty">
              <Activity size={22} />
              <p>{planning ? "正在规划任务" : "等待任务规划"}</p>
              <span>
                {planning
                  ? "正在梳理研究目标与分析步骤。"
                  : "发送研究问题后，计划步骤将在这里显示。"}
              </span>
            </div>
          )}
          {activities.length > 0 && (
            <section aria-label="执行活动" className="workspace-action-stream">
              <div className="workspace-action-heading">
                <Activity size={14} />
                <strong>执行活动</strong>
                <span>{activities.length}</span>
              </div>
              <ol className="workspace-activity-list workspace-activity-stream">
                {activities.map((activity) => (
                  <ExecutionActivityRow
                    key={activity.id}
                    activity={activity}
                    currentTime={0}
                    preparationBadges={new Map()}
                    hideTiming
                  />
                ))}
              </ol>
            </section>
          )}
        </div>
      </section>
      <WorkspaceFilesPanel
        sessionId="public-demo"
        refreshKey={String(phase)}
        expanded={expanded}
        onToggle={() => setExpanded((value) => !value)}
        ready
        fileRequest={fileRequest}
        onFileRequestHandled={onFileRequestHandled}
        listFiles={listFiles}
      />
    </aside>
  );
}
